import { Injectable, computed, signal } from '@angular/core';

import { esAppInstalada, esIos } from '../utils/plataforma';

/** Evento de Chrome/Edge/Samsung Internet. No está en los tipos del DOM. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstalarAppModo = 'android' | 'ios';

/**
 * Android: el navegador avisa con `beforeinstallprompt` y la app muestra su diálogo nativo.
 * iOS: no hay evento ni diálogo; se explica cómo usar Compartir > Agregar a inicio.
 * Tiene que crearse al iniciar la app: el evento de Android llega una sola vez y temprano.
 */
@Injectable({ providedIn: 'root' })
export class InstalarAppService {
  private promptAndroid: BeforeInstallPromptEvent | null = null;

  private readonly androidDisponible = signal(false);
  private readonly instalada = signal(esAppInstalada());
  private readonly iosDisponible = esIos();

  readonly modo = computed<InstalarAppModo | null>(() => {
    if (this.instalada()) return null;
    if (this.androidDisponible()) return 'android';
    if (this.iosDisponible) return 'ios';
    return null;
  });

  constructor() {
    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.promptAndroid = event as BeforeInstallPromptEvent;
      this.androidDisponible.set(true);
    });

    window.addEventListener('appinstalled', () => {
      this.promptAndroid = null;
      this.androidDisponible.set(false);
      this.instalada.set(true);
    });
  }

  /** Solo Android. En iOS la pantalla muestra las instrucciones. */
  async instalarAndroid(): Promise<void> {
    const prompt = this.promptAndroid;
    if (!prompt) return;

    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    this.promptAndroid = null;
    this.androidDisponible.set(false);
    if (outcome === 'accepted') {
      this.instalada.set(true);
    }
  }
}
